/**
 * DbRadio - 单选组件
 *
 * 特性：
 * - 支持左右布局（左侧选择框右侧文字 / 左侧文字右侧选择框）
 * - 整行可点击，提升用户体验
 * - 支持描述文字
 * - 支持自定义选中/未选中图标
 * - 支持受控和非受控模式
 * - 支持自定义 Radio 样式（大小、边框、颜色等）
 * - 支持自定义 label 样式和内容
 */

import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';
import { View, Text, TouchableOpacity, ViewStyle } from 'react-native';
import {
    DbRadioProps,
    DbRadioItemProps,
    DbRadioGroupProps,
    RadioOption,
    RadioLayout,
    RadioStyleConfig,
} from './DbRadio.types';
import { createStyles, getSizeConfig } from './DbRadio.styles';

// ==================== Context ====================

interface RadioContextValue {
    value?: any;
    onChange?: (value: any) => void;
    layout?: RadioLayout;
    size?: 'small' | 'medium' | 'large';
    activeColor?: string;
    inactiveColor?: string;
    radioConfig?: RadioStyleConfig;
    disabled?: boolean;
    labelStyle?: any;
    descriptionStyle?: any;
    labelColor?: string;
    descriptionColor?: string;
    renderChecked?: () => React.ReactNode;
    renderUnchecked?: () => React.ReactNode;
}

const RadioContext = createContext<RadioContextValue>({});

// ==================== RadioItem 组件 ====================

export const DbRadioItem = <T extends any = string>({
    value,
    label,
    labelContent,
    checked: checkedProp,
    disabled = false,
    description,
    descriptionContent,
    layout = 'left',
    size = 'medium',
    activeColor = '#007AFF',
    inactiveColor,
    radioConfig,
    onPress,
    style,
    labelStyle,
    descriptionStyle,
    labelColor,
    descriptionColor,
    renderChecked,
    renderUnchecked,
}: DbRadioItemProps<T>) => {
    // 从 Context 获取配置
    const context = useContext(RadioContext);
    
    // 合并配置
    const finalLayout = context.layout ?? layout;
    const finalSize = context.size ?? size;
    const finalActiveColor = context.activeColor ?? activeColor;
    const finalInactiveColor = context.inactiveColor ?? inactiveColor;
    const finalRadioConfig = context.radioConfig ?? radioConfig;
    const finalDisabled = context.disabled || disabled;
    const finalLabelStyle = context.labelStyle ?? labelStyle;
    const finalDescriptionStyle = context.descriptionStyle ?? descriptionStyle;
    const finalLabelColor = context.labelColor ?? labelColor;
    const finalDescriptionColor = context.descriptionColor ?? descriptionColor;
    const finalRenderChecked = context.renderChecked ?? renderChecked;
    const finalRenderUnchecked = context.renderUnchecked ?? renderUnchecked;
    
    // 判断是否选中
    const isChecked = checkedProp !== undefined 
        ? checkedProp 
        : context.value === value;

    const styles = useMemo(
        () => createStyles(finalSize, finalActiveColor),
        [finalSize, finalActiveColor]
    );

    const sizeConfig = useMemo(() => getSizeConfig(finalSize), [finalSize]);

    const handlePress = useCallback(() => {
        if (finalDisabled) return;
        
        if (context.onChange) {
            context.onChange(value);
        } else if (onPress) {
            onPress(value);
        }
    }, [finalDisabled, context, value, onPress]);

    // 渲染 Radio 圆圈
    const renderRadio = () => {
        if (isChecked && finalRenderChecked) {
            return finalRenderChecked();
        }
        if (!isChecked && finalRenderUnchecked) {
            return finalRenderUnchecked();
        }

        // 计算 Radio 样式
        const radioSize = finalRadioConfig?.radioSize ?? sizeConfig.radioSize;
        const innerSize = finalRadioConfig?.innerSize ?? sizeConfig.innerSize;
        const borderWidth = finalRadioConfig?.borderWidth ?? 2;
        const borderColor = isChecked 
            ? (finalRadioConfig?.activeBorderColor ?? finalActiveColor)
            : (finalRadioConfig?.borderColor ?? finalInactiveColor ?? '#D1D1D6');
        const innerColor = finalRadioConfig?.innerColor ?? finalActiveColor;

        return (
            <View
                style={[
                    styles.radio,
                    {
                        width: radioSize,
                        height: radioSize,
                        borderRadius: radioSize / 2,
                        borderWidth,
                        borderColor: finalDisabled ? '#E5E5EA' : borderColor,
                    },
                    finalRadioConfig?.radioStyle,
                ]}
            >
                {isChecked && (
                    <View
                        style={[
                            styles.radioInner,
                            {
                                width: innerSize,
                                height: innerSize,
                                borderRadius: innerSize / 2,
                                backgroundColor: finalDisabled ? '#E5E5EA' : innerColor,
                            },
                        ]}
                    />
                )}
            </View>
        );
    };

    // 渲染文字内容
    const renderTextContent = () => (
        <View
            style={[
                styles.textContainer,
                finalLayout === 'left' ? styles.textContainerLeft : styles.textContainerRight,
            ]}
        >
            {/* 标签内容 */}
            {labelContent ? (
                labelContent
            ) : label ? (
                <Text
                    style={[
                        styles.label,
                        finalLabelColor && { color: finalLabelColor },
                        finalDisabled && styles.labelDisabled,
                        finalLabelStyle,
                    ]}
                    numberOfLines={1}
                >
                    {label}
                </Text>
            ) : null}
            
            {/* 描述内容 */}
            {descriptionContent ? (
                descriptionContent
            ) : description ? (
                <Text
                    style={[
                        styles.description,
                        finalDescriptionColor && { color: finalDescriptionColor },
                        finalDisabled && styles.descriptionDisabled,
                        finalDescriptionStyle,
                    ]}
                    numberOfLines={2}
                >
                    {description}
                </Text>
            ) : null}
        </View>
    );

    // 计算 padding
    const itemPadding = finalRadioConfig?.itemPadding ?? 16;
    const itemPaddingVertical = finalRadioConfig?.itemPaddingVertical ?? 12;

    return (
        <TouchableOpacity
            style={[
                styles.itemContainer,
                finalLayout === 'left' ? styles.itemContainerLeft : styles.itemContainerRight,
                finalDisabled && styles.itemContainerDisabled,
                {
                    paddingHorizontal: itemPadding,
                    paddingVertical: itemPaddingVertical,
                },
                style,
            ]}
            onPress={handlePress}
            disabled={finalDisabled}
            activeOpacity={0.6}
        >
            {renderRadio()}
            {renderTextContent()}
        </TouchableOpacity>
    );
};

// ==================== RadioGroup 组件 ====================

export const DbRadioGroup = <T extends any = string>({
    value: valueProp,
    defaultValue,
    onChange,
    options,
    disabled = false,
    layout = 'left',
    size = 'medium',
    activeColor = '#007AFF',
    inactiveColor,
    radioConfig,
    spacing = 0,
    style,
    itemStyle,
    labelStyle,
    descriptionStyle,
    labelColor,
    descriptionColor,
    renderChecked,
    renderUnchecked,
    children,
}: DbRadioGroupProps<T>) => {
    // 非受控模式的内部状态
    const [internalValue, setInternalValue] = useState<T | undefined>(defaultValue);
    
    // 最终值
    const currentValue = valueProp !== undefined ? valueProp : internalValue;

    const handleChange = useCallback(
        (newValue: T) => {
            if (valueProp === undefined) {
                setInternalValue(newValue);
            }
            onChange?.(newValue);
        },
        [valueProp, onChange]
    );

    const contextValue = useMemo<RadioContextValue>(
        () => ({
            value: currentValue,
            onChange: handleChange,
            layout,
            size,
            activeColor,
            inactiveColor,
            radioConfig,
            disabled,
            labelStyle,
            descriptionStyle,
            labelColor,
            descriptionColor,
            renderChecked,
            renderUnchecked,
        }),
        [currentValue, handleChange, layout, size, activeColor, inactiveColor, radioConfig, disabled, labelStyle, descriptionStyle, labelColor, descriptionColor, renderChecked, renderUnchecked]
    );

    const styles = useMemo(() => createStyles(size, activeColor), [size, activeColor]);

    // 渲染选项列表
    const renderOptions = () => {
        if (options && options.length > 0) {
            return options.map((option, index) => (
                <React.Fragment key={String(option.value)}>
                    <DbRadioItem<T>
                        value={option.value}
                        label={option.label}
                        labelContent={option.labelContent}
                        disabled={option.disabled}
                        description={option.description}
                        descriptionContent={option.descriptionContent}
                        style={[
                            index > 0 && spacing > 0 ? { marginTop: spacing } as ViewStyle : undefined,
                            itemStyle,
                        ]}
                    />
                    {index < options.length - 1 && spacing === 0 && (
                        <View style={styles.separator} />
                    )}
                </React.Fragment>
            ));
        }

        // 使用 children
        return children;
    };

    return (
        <RadioContext.Provider value={contextValue}>
            <View style={[styles.container, style]}>
                {renderOptions()}
            </View>
        </RadioContext.Provider>
    );
};

// ==================== DbRadio 主组件 ====================

const DbRadio = <T extends any = string>(props: DbRadioProps<T>) => {
    return <DbRadioGroup<T> {...props} />;
};

DbRadio.displayName = 'DbRadio';
DbRadio.Item = DbRadioItem;
DbRadio.Group = DbRadioGroup;

export default DbRadio;
export type {
    DbRadioProps,
    DbRadioItemProps,
    DbRadioGroupProps,
    RadioOption,
    RadioLayout,
    RadioStyleConfig,
} from './DbRadio.types';
